import React, { useMemo, useState } from 'react';
import {
    completeCancellationSurvey,
    saveCancellationSurveyAnswer,
    surveyRequestErrorMessage,
    surveyRequestIsAlreadyComplete,
} from '../utils/cancellationSurveyApi';
import './ContractWizard.css';

function currentValue(question, answers, draft) {
    if (draft && Object.prototype.hasOwnProperty.call(draft, question.key)) {
        return draft[question.key];
    }
    return answers?.[question.key]?.value;
}

function parseStoredOtherDetails(otherText) {
    if (otherText && typeof otherText === 'object' && !Array.isArray(otherText)) {
        return otherText;
    }
    if (typeof otherText === 'string' && otherText.trim().startsWith('{')) {
        try {
            const parsed = JSON.parse(otherText);
            if (parsed && typeof parsed === 'object') return parsed;
        } catch {
            return { other: otherText };
        }
    }
    if (typeof otherText === 'string' && otherText) {
        return { other: otherText };
    }
    return {};
}

function currentOtherDetails(question, answers, draftOther) {
    const saved = parseStoredOtherDetails(answers?.[question.key]?.other_text);
    const draft = draftOther?.[question.key] || {};
    return { ...saved, ...draft };
}

function SurveyWizardHeader() {
    return (
        <div className="wizard-header survey-wizard-header">
            <img
                className="survey-header-logo"
                src={`${process.env.PUBLIC_URL || ''}/keyflow_logo.png`}
                alt="KeyFlo"
            />
        </div>
    );
}

export default function CancellationSurveyWizard({ token, session, onSessionChange, initialStep = 0 }) {
    const questions = session?.questions || [];
    const answers = session?.answers || {};
    const [step, setStep] = useState(initialStep);
    const [draft, setDraft] = useState({});
    const [draftOther, setDraftOther] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(session?.status === 'completed');

    const question = questions[step];
    const isLast = step === questions.length - 1;
    const progress = questions.length ? ((step + 1) / questions.length) * 100 : 0;

    const value = question ? currentValue(question, answers, draft) : undefined;
    const otherDetails = question ? currentOtherDetails(question, answers, draftOther) : {};
    const otherText = otherDetails.other || '';
    const optionalText = otherDetails.switched_tool || '';

    const canProceed = useMemo(() => {
        if (!question) return false;
        if (question.type === 'nps') {
            return Number.isInteger(value) && value >= 0 && value <= 10;
        }
        if (question.type === 'single') {
            return Boolean(value);
        }
        if (question.type === 'multi') {
            if (!Array.isArray(value) || !value.length) return false;
            if (question.max_select && value.length > question.max_select) return false;
            if (value.includes(question.other_value) && !String(otherText).trim()) return false;
            return true;
        }
        return true;
    }, [question, value, otherText]);

    const persistCurrent = async () => {
        if (!question) return session;
        const details = {};
        if (question.type === 'multi' && Array.isArray(value)) {
            if (value.includes(question.other_value) && otherText.trim()) {
                details.other = otherText.trim();
            }
            if (value.includes(question.optional_text_value) && optionalText.trim()) {
                details.switched_tool = optionalText.trim();
            }
        }
        const payload = {
            question_key: question.key,
            value: question.type === 'text' ? (value || '') : value,
            other_text: Object.keys(details).length ? details : undefined,
        };
        if (question.type === 'text' && !String(value || '').trim() && question.required) {
            return session;
        }
        const next = await saveCancellationSurveyAnswer(token, payload);
        onSessionChange?.(next);
        return next;
    };

    const handleNext = async () => {
        try {
            setSubmitting(true);
            setError(null);
            await persistCurrent();
            if (isLast) {
                const completed = await completeCancellationSurvey(token);
                onSessionChange?.(completed);
                setDone(true);
            } else {
                setStep((s) => s + 1);
            }
        } catch (err) {
            if (surveyRequestIsAlreadyComplete(err)) {
                onSessionChange?.({ ...(session || {}), status: 'completed' });
                setDone(true);
                return;
            }
            setError(surveyRequestErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (done || session?.status === 'completed') {
        return (
            <div className="wizard-overlay wizard-page-mode cancellation-survey-wizard">
                <div className="wizard-container">
                    <SurveyWizardHeader />
                    <div className="wizard-content">
                        <p className="survey-thank-you-copy">
                            Thank you for taking the time to provide this feedback. You can close this page.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!question) {
        return null;
    }

    const toggleMulti = (optionValue) => {
        const current = Array.isArray(value) ? [...value] : [];
        const alreadySelected = current.includes(optionValue);
        if (
            !alreadySelected
            && question.max_select
            && current.length >= question.max_select
        ) {
            return;
        }
        const next = alreadySelected
            ? current.filter((item) => item !== optionValue)
            : [...current, optionValue];
        setDraft((prev) => ({ ...prev, [question.key]: next }));
    };

    return (
        <div className="wizard-overlay wizard-page-mode cancellation-survey-wizard">
            <div className="wizard-container">
                <SurveyWizardHeader />
                <div className="wizard-progress">
                    <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${progress}%` }} />
                    </div>
                    <span>Question {step + 1} of {questions.length}</span>
                </div>
                <div className="wizard-content">
                    {error && <p className="subscription-modal-error">{error}</p>}
                    <h3 className="wizard-question">{question.prompt}</h3>
                    {question.type === 'multi' && question.max_select ? (
                        <p className="survey-helper-copy">
                            Select up to {question.max_select}
                            {Array.isArray(value) && value.length
                                ? ` (${value.length} selected)`
                                : ''}
                        </p>
                    ) : null}

                    {question.type === 'nps' && (
                        <div className="survey-nps-row">
                            {Array.from({ length: 11 }, (_, score) => (
                                <button
                                    key={score}
                                    type="button"
                                    className={`wizard-button survey-nps-btn${value === score ? ' active' : ''}`}
                                    onClick={() => setDraft((prev) => ({ ...prev, [question.key]: score }))}
                                >
                                    {score}
                                </button>
                            ))}
                        </div>
                    )}

                    {question.type === 'single' && (
                        <div className="survey-choice-list">
                            {question.options.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`wizard-button${value === opt.value ? ' active' : ''}`}
                                    onClick={() => setDraft((prev) => ({ ...prev, [question.key]: opt.value }))}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {question.type === 'multi' && (
                        <div className="survey-choice-list">
                            {question.options.map((opt) => {
                                const selected = Array.isArray(value) && value.includes(opt.value);
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`wizard-button${selected ? ' active' : ''}`}
                                        onClick={() => toggleMulti(opt.value)}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                            {Array.isArray(value) && value.includes(question.other_value) && (
                                <textarea
                                    className="subscription-cancel-textarea"
                                    rows={3}
                                    placeholder="Please describe your other reason"
                                    value={otherText}
                                    onChange={(e) => setDraftOther((prev) => ({
                                        ...prev,
                                        [question.key]: { ...otherDetails, other: e.target.value },
                                    }))}
                                />
                            )}
                            {Array.isArray(value) && value.includes(question.optional_text_value) && (
                                <input
                                    className="subscription-cancel-textarea"
                                    placeholder={question.optional_text_label || 'Optional details'}
                                    value={optionalText}
                                    onChange={(e) => setDraftOther((prev) => ({
                                        ...prev,
                                        [question.key]: { ...otherDetails, switched_tool: e.target.value },
                                    }))}
                                />
                            )}
                        </div>
                    )}

                    {question.type === 'text' && (
                        <textarea
                            className="subscription-cancel-textarea"
                            rows={4}
                            placeholder="Optional"
                            value={value || ''}
                            onChange={(e) => setDraft((prev) => ({ ...prev, [question.key]: e.target.value }))}
                        />
                    )}
                </div>
                <div className="wizard-actions wizard-sticky-footer">
                    <button
                        type="button"
                        className="wizard-button secondary"
                        onClick={() => setStep((s) => Math.max(0, s - 1))}
                        disabled={step === 0 || submitting}
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        className="wizard-button"
                        onClick={handleNext}
                        disabled={!canProceed || submitting}
                    >
                        {submitting ? 'Saving…' : isLast ? 'Finish' : 'Next'}
                    </button>
                </div>
            </div>
        </div>
    );
}
